// src/pages/Enterprise.js
//
// Enterprise detail page. Owns the breadcrumb, back arrow, header,
// and tab switcher. Tabs: "Program", "Orchestration Fees", "Agent Access", "Travel",
// "Travel channel" (GOV-TRAVEL-CHANNEL-PANEL-01: how this customer's flights get booked).
//
// The org is resolved on mount from the URL param (slug or _id) against the
// T1 org list, so the header reflects the real org and the Agent Access tab
// receives the real Mongo _id (not the slug).
//
// Tab state is reflected in the URL query string (?tab=fees) for deep linking.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Breadcrumbs,
    Link as MuiLink,
    Tabs,
    Tab,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import EnterpriseProgramTab from './enterprise/EnterpriseProgramTab';
import EnterpriseFeesTab from './enterprise/EnterpriseFeesTab';
import EnterpriseAgentAccessTab from './enterprise/EnterpriseAgentAccessTab';
import EnterpriseTravelTab from './enterprise/EnterpriseTravelTab';
import EnterpriseTravelChannelTab from './enterprise/EnterpriseTravelChannelTab';
// Org lookup now goes through the authed T6 admin proxy (Firebase bearer), reusing
// the exported base + auth from the Agent Access tab.
import { T6_BASE, authHeader } from './enterprise/EnterpriseAgentAccessTab';

const Header = styled('div')({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem"
});

const TAB_KEYS = ['program', 'fees', 'agents', 'travel', 'channel'];

function getTabFromSearch(search) {
    const params = new URLSearchParams(search);
    const t = params.get('tab');
    return TAB_KEYS.includes(t) ? t : 'program';
}

export default function Enterprise() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [tab, setTab] = useState(getTabFromSearch(location.search));

    const [org, setOrg] = useState(null);
    const [orgLoading, setOrgLoading] = useState(true);
    const [orgError, setOrgError] = useState(null);

    useEffect(() => {
        setTab(getTabFromSearch(location.search));
    }, [location.search]);

    // Resolve the real org from the URL param. The Enterprises list links with
    // `slug || _id`, so the param could be either — match on both.
    const fetchOrg = useCallback(async () => {
        setOrgLoading(true);
        setOrgError(null);
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/admin/orgs`, { headers });
            const list = Array.isArray(res.data) ? res.data : (res.data?.orgs ?? []);
            const found = list.find((o) => o.slug === id || o._id === id);
            if (!found) {
                setOrg(null);
                setOrgError(`Organization "${id}" not found.`);
            } else {
                setOrg(found);
            }
        } catch (err) {
            setOrg(null);
            setOrgError(err.response?.data?.message || err.message || 'Failed to load organization.');
        } finally {
            setOrgLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrg();
    }, [fetchOrg]);

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        const params = new URLSearchParams(location.search);
        if (newValue === 'program') {
            params.delete('tab');
        } else {
            params.set('tab', newValue);
        }
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
    };

    if (orgLoading) {
        return (
            <Box sx={{ padding: "2rem", color: "#fff", backgroundColor: "#101010", minHeight: "100vh", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#12c099' }} />
            </Box>
        );
    }

    if (orgError || !org) {
        return (
            <Box sx={{ padding: "2rem", color: "#fff", backgroundColor: "#101010", minHeight: "100vh" }}>
                <Alert
                    severity="error"
                    sx={{ bgcolor: 'rgba(255,107,107,0.1)', color: '#ff6b6b', border: '1px solid rgba(255,107,107,0.3)' }}
                    action={<Button color="inherit" size="small" onClick={fetchOrg}>Retry</Button>}
                >
                    {orgError || 'Organization not found.'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: "2rem", color: "#fff", backgroundColor: "#101010", minHeight: "100vh" }}>

            {/* BREADCRUMB */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs sx={{ color: '#666', fontSize: '0.85rem' }}>
                    <MuiLink
                        component={Link}
                        to="/enterprises"
                        sx={{ color: '#12c099', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Enterprises
                    </MuiLink>
                    <Typography sx={{ color: '#fff' }}>{org.name}</Typography>
                </Breadcrumbs>
            </Box>

            {/* HEADER */}
            <Header>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        component={Link}
                        to="/enterprises"
                        sx={{ color: '#888', '&:hover': { color: '#fff' } }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {org.name}
                        </Typography>
                        <Typography sx={{ color: "#aaa" }}>
                            Corporate Experience Program Overview
                        </Typography>
                    </Box>
                </Box>

            </Header>

            {/* TABS */}
            <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)', mb: 3 }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': {
                            color: '#888',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: '#12c099' }
                        },
                        '& .MuiTabs-indicator': { backgroundColor: '#12c099', height: 3 }
                    }}
                >
                    <Tab value="program" label="Program" />
                    <Tab value="fees" label="Orchestration Fees" />
                    <Tab value="agents" label="Agent Access" />
                    <Tab value="travel" label="Travel" />
                    <Tab value="channel" label="Travel channel" />
                </Tabs>
            </Box>

            {/* TAB CONTENT */}
            {tab === 'program' && <EnterpriseProgramTab orgSlug={org.slug || org._id} />}
            {tab === 'fees' && <EnterpriseFeesTab />}
            {tab === 'agents' && org && <EnterpriseAgentAccessTab orgId={org._id} orgName={org.name} />}
            {tab === 'travel' && org && <EnterpriseTravelTab orgId={org._id} />}
            {tab === 'channel' && org && <EnterpriseTravelChannelTab orgId={org._id} />}
        </Box>
    );
}
